/**
 * Financial Dashboard Page
 * STRICT SUPERADMIN / FINANCIAL ACCESS ONLY
 * Stripe-Ready Financial Tracking & AI Manager
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Gamepad2,
  RefreshCw,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Info,
  Sparkles,
  Lock,
  Shield,
  ChevronDown,
  Download,
  Brain,
  Lightbulb,
  Target,
  AlertCircle,
  BarChart3,
  PieChart,
  Gem
} from 'lucide-react';

const FinancialDashboardPage = () => {
  const { api, admin } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [revenueData, setRevenueData] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [creatorData, setCreatorData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [generatingData, setGeneratingData] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      fetchAllData();
    }
  }, [hasAccess, period]);

  const checkAccess = () => {
    // Check if user has financial access
    const role = admin?.role;
    const financialAccess = admin?.financial_access;
    
    if (role === 'super_admin' || financialAccess) {
      setHasAccess(true);
    } else {
      setHasAccess(false);
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [revenue, tokens, creators, costs, txns, insights] = await Promise.all([
        api.get(`/api/admin/financials/dashboard/revenue?period=${period}`).catch(() => ({ data: null })),
        api.get('/api/admin/financials/dashboard/token-economy').catch(() => ({ data: null })),
        api.get('/api/admin/financials/dashboard/creator-economics').catch(() => ({ data: null })),
        api.get(`/api/admin/financials/dashboard/costs?period=${period}`).catch(() => ({ data: null })),
        api.get('/api/admin/financials/transactions?page_size=20').catch(() => ({ data: { transactions: [] } })),
        api.get('/api/admin/financials/ai-manager/insights').catch(() => ({ data: { insights: [] } }))
      ]);
      
      setRevenueData(revenue.data);
      setTokenData(tokens.data);
      setCreatorData(creators.data);
      setCostData(costs.data);
      setTransactions(txns.data?.transactions || []);
      setAiInsights(insights.data?.insights || []);
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const generateSimulatedData = async () => {
    if (!window.confirm('Generate 30 days of simulated financial data for testing? This will create ~1500 transactions.')) {
      return;
    }
    
    setGeneratingData(true);
    try {
      await api.post('/api/admin/financials/simulate/generate-data', {
        days: 30,
        transactions_per_day: 50
      });
      toast.success('Simulated data generated successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to generate data');
    } finally {
      setGeneratingData(false);
    }
  };

  // Access Denied View
  if (!hasAccess && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <Lock className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Financial Access Required</h1>
          <p className="text-gray-600 mb-6">
            You do not have permission to view financial data. This section is restricted to Super Admins and admins with explicit Financial Access.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Need access?</strong> Contact a Super Admin to request Financial Access permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading View
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  const MetricCard = ({ title, value, change, changeType, icon: Icon, color = "emerald" }) => (
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        {change !== undefined && (
          <div className={`flex items-center ${changeType === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {changeType === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
            <span className="text-sm font-medium">{change}%</span>
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );

  const InsightCard = ({ insight }) => {
    const categoryColors = {
      revenue: 'emerald',
      token_economy: 'blue',
      creator: 'purple',
      retention: 'amber',
      risk: 'red'
    };
    const color = categoryColors[insight.category] || 'gray';
    
    const confidenceColors = {
      high: 'green',
      medium: 'amber',
      low: 'gray'
    };
    
    return (
      <div className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
            <Lightbulb className={`h-5 w-5 text-${color}-600`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700 capitalize`}>
                {insight.category?.replace('_', ' ')}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full bg-${confidenceColors[insight.confidence_level]}-100 text-${confidenceColors[insight.confidence_level]}-700`}>
                {insight.confidence_level} confidence
              </span>
            </div>
            <p className="text-sm text-gray-800 mb-2">{insight.summary}</p>
            <div className="bg-gray-50 rounded p-2 mb-2">
              <p className="text-xs text-gray-600">
                <strong>Suggested Action:</strong> {insight.suggested_action}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Financial Dashboard</h1>
            <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
              <Shield className="h-3 w-3" />
              {admin?.role === 'super_admin' ? 'Super Admin' : 'Financial Access'}
            </span>
          </div>
          <p className="text-gray-600 mt-1">Stripe-ready financial tracking and AI insights</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          
          <button
            onClick={fetchAllData}
            className="px-4 py-2 bg-white border rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Live Mode Notice */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-emerald-600 mt-0.5" />
        <div>
          <p className="text-sm text-emerald-800 font-medium">Live Mode Active</p>
          <p className="text-sm text-emerald-700">
            Financial data reflects real transactions via PayPal Commerce Platform. 
            All Emerald recharges and gifts are tracked in real-time.
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <div className="flex gap-1">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'token-economy', label: 'Token Economy', icon: Wallet },
            { id: 'economics', label: 'Creator & Player Economics', icon: Users },
            { id: 'costs', label: 'Cost Analysis', icon: PieChart },
            { id: 'transactions', label: 'Transaction Ledger', icon: CreditCard },
            { id: 'ai-manager', label: 'AI Manager', icon: Brain }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && revenueData && (
        <div className="space-y-6">
          {/* Revenue Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Gross Revenue"
              value={`$${revenueData.metrics?.gross_revenue?.toLocaleString() || '0'}`}
              icon={DollarSign}
              color="emerald"
            />
            <MetricCard
              title="Platform Service Fees"
              value={`$${revenueData.metrics?.platform_service_fees?.toLocaleString() || '0'}`}
              icon={TrendingUp}
              color="blue"
            />
            <MetricCard
              title="Match Entry Revenue"
              value={`$${revenueData.metrics?.match_entry_revenue?.toLocaleString() || '0'}`}
              icon={Gamepad2}
              color="purple"
            />
            <MetricCard
              title="Tips & Gifts"
              value={`$${revenueData.metrics?.tips_gifts_revenue?.toLocaleString() || '0'}`}
              icon={Wallet}
              color="amber"
            />
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Token Sales"
              value={`$${revenueData.metrics?.token_sales_revenue?.toLocaleString() || '0'}`}
              icon={CreditCard}
              color="teal"
            />
            <MetricCard
              title="Refunds"
              value={`$${revenueData.metrics?.refunds?.toLocaleString() || '0'}`}
              icon={TrendingDown}
              color="red"
            />
            <MetricCard
              title="Chargebacks"
              value={`$${revenueData.metrics?.chargebacks?.toLocaleString() || '0'}`}
              icon={AlertTriangle}
              color="red"
            />
          </div>

          {/* Transaction Counts */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction Volume</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(revenueData.transaction_counts || {}).map(([type, count]) => (
                <div key={type} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500 capitalize">{type.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Token Economy Tab */}
      {activeTab === 'token-economy' && tokenData && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Gem className="w-6 h-6 text-emerald-100" />
                </div>
                <div>
                  <p className="text-emerald-100">Total Emeralds in Circulation</p>
                  <p className="text-3xl font-bold">{tokenData.token_balances?.total_emeralds_in_circulation?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Gem className="w-6 h-6 text-red-100" />
                </div>
                <div>
                  <p className="text-red-100">Total Rubies in Circulation</p>
                  <p className="text-3xl font-bold">{tokenData.token_balances?.total_rubies_in_circulation?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-amber-100">Outstanding Ruby Liability</p>
                  <p className="text-3xl font-bold">${tokenData.token_balances?.outstanding_ruby_liability_usd?.toLocaleString() || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Token Flow */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Token Flow</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-sm text-gray-500">Emeralds Purchased</p>
                <p className="text-2xl font-bold text-emerald-600">{tokenData.token_flow?.emeralds_purchased?.toLocaleString() || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Emeralds Spent</p>
                <p className="text-2xl font-bold text-gray-900">{tokenData.token_flow?.emeralds_spent?.toLocaleString() || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Rubies Issued</p>
                <p className="text-2xl font-bold text-red-600">{tokenData.token_flow?.rubies_issued?.toLocaleString() || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-500">Rubies Redeemed</p>
                <p className="text-2xl font-bold text-gray-900">{tokenData.token_flow?.rubies_redeemed?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>

          {/* Averages */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Averages</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Avg Emeralds/Match</p>
                <p className="text-xl font-bold">{tokenData.averages?.avg_emeralds_per_match || 0}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Avg Rubies/Payout</p>
                <p className="text-xl font-bold">{tokenData.averages?.avg_rubies_per_payout || 0}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Avg Emeralds/User</p>
                <p className="text-xl font-bold">{tokenData.averages?.avg_emeralds_per_user || 0}</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Avg Rubies/User</p>
                <p className="text-xl font-bold">{tokenData.averages?.avg_rubies_per_user || 0}</p>
              </div>
            </div>
          </div>

          {/* Virtual Token Disclaimer */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 text-center">
              Emeralds and Rubies are virtual tokens used within the GEMS Live platform. They are not a currency, stored value, or financial account.
            </p>
          </div>
        </div>
      )}

      {/* Creator & Player Economics Tab */}
      {activeTab === 'economics' && creatorData && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Creator Metrics */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Creator Metrics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Creators</span>
                  <span className="font-bold text-xl">{creatorData.creator_metrics?.total_creators || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Gross Earnings</span>
                  <span className="font-bold text-xl text-emerald-600">${creatorData.creator_metrics?.gross_earnings?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Platform Fees Paid</span>
                  <span className="font-bold text-xl">${creatorData.creator_metrics?.platform_fees_paid?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Net Payouts</span>
                  <span className="font-bold text-xl text-green-600">${creatorData.creator_metrics?.net_payouts?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-purple-700">Avg Revenue/Stream</span>
                  <span className="font-bold text-xl text-purple-600">${creatorData.creator_metrics?.avg_revenue_per_stream?.toFixed(2) || 0}</span>
                </div>
              </div>
            </div>

            {/* Player Metrics */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Gamepad2 className="h-5 w-5 text-blue-600" />
                Player Metrics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Players</span>
                  <span className="font-bold text-xl">{creatorData.player_metrics?.total_players || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Matches Played</span>
                  <span className="font-bold text-xl">{creatorData.player_metrics?.total_matches_played || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Total Match Entry Spent</span>
                  <span className="font-bold text-xl">${creatorData.player_metrics?.total_match_entry_spent?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Avg Match Entry</span>
                  <span className="font-bold text-xl">${creatorData.player_metrics?.avg_match_entry?.toFixed(2) || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-blue-700">Win/Loss Ratio (Avg)</span>
                  <span className="font-bold text-xl text-blue-600">{creatorData.player_metrics?.avg_win_loss_ratio?.toFixed(2) || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Analysis Tab */}
      {activeTab === 'costs' && costData && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              title="Total Estimated Costs"
              value={`$${costData.totals?.total_estimated_costs?.toLocaleString() || 0}`}
              icon={TrendingDown}
              color="red"
            />
            <MetricCard
              title="Gross Revenue"
              value={`$${costData.totals?.gross_revenue?.toLocaleString() || 0}`}
              icon={TrendingUp}
              color="emerald"
            />
            <MetricCard
              title="Revenue/Cost Ratio"
              value={`${costData.totals?.revenue_cost_ratio?.toFixed(2) || 0}x`}
              icon={Target}
              color="blue"
            />
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Cost Breakdown</h3>
            <div className="space-y-4">
              {/* PayPal Processing */}
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div>
                  <p className="font-medium text-blue-900">PayPal Processing</p>
                  <p className="text-sm text-blue-700">{costData.cost_breakdown?.paypal_processing?.transactions || 0} transactions • ${costData.cost_breakdown?.paypal_processing?.volume_processed || 0} processed</p>
                  <p className="text-xs text-blue-600 mt-1">{costData.cost_breakdown?.paypal_processing?.rate}</p>
                </div>
                <span className="font-bold text-lg text-blue-900">${costData.cost_breakdown?.paypal_processing?.estimated_fees?.toFixed(2) || 0}</span>
              </div>
              
              {/* Firebase */}
              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div>
                  <p className="font-medium text-orange-900">Firebase</p>
                  <p className="text-sm text-orange-700">{costData.cost_breakdown?.firebase?.sms_verifications || 0} SMS verifications • {costData.cost_breakdown?.firebase?.firestore_operations || 0} DB ops</p>
                  <p className="text-xs text-orange-600 mt-1">{costData.cost_breakdown?.firebase?.rate}</p>
                </div>
                <span className="font-bold text-lg text-orange-900">${costData.cost_breakdown?.firebase?.total_cost?.toFixed(2) || 0}</span>
              </div>
              
              {/* Agora */}
              <div className="flex justify-between items-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div>
                  <p className="font-medium text-purple-900">Agora (Multiplayer)</p>
                  <p className="text-sm text-purple-700">{costData.cost_breakdown?.agora_usage?.sessions || 0} sessions • {costData.cost_breakdown?.agora_usage?.total_minutes || 0} minutes</p>
                  <p className="text-xs text-purple-600 mt-1">{costData.cost_breakdown?.agora_usage?.rate}</p>
                </div>
                <span className="font-bold text-lg text-purple-900">${costData.cost_breakdown?.agora_usage?.cost?.toFixed(2) || 0}</span>
              </div>
              
              {/* LiveKit */}
              <div className="flex justify-between items-center p-4 bg-teal-50 rounded-lg border border-teal-100">
                <div>
                  <p className="font-medium text-teal-900">LiveKit (Solo Streams)</p>
                  <p className="text-sm text-teal-700">{costData.cost_breakdown?.livekit_usage?.streams || 0} streams • ~{costData.cost_breakdown?.livekit_usage?.estimated_minutes || 0} minutes</p>
                  <p className="text-xs text-teal-600 mt-1">{costData.cost_breakdown?.livekit_usage?.rate}</p>
                </div>
                <span className="font-bold text-lg text-teal-900">${costData.cost_breakdown?.livekit_usage?.estimated_cost?.toFixed(2) || 0}</span>
              </div>
              
              {/* Storage */}
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="font-medium text-gray-900">Storage & Database</p>
                  <p className="text-sm text-gray-600">MongoDB, media storage</p>
                </div>
                <span className="font-bold text-lg text-gray-900">${(costData.cost_breakdown?.storage || 0).toFixed(2)}</span>
              </div>
              
              {/* Infrastructure */}
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <p className="font-medium text-gray-900">Infrastructure</p>
                  <p className="text-sm text-gray-600">Cloud hosting, CDN</p>
                </div>
                <span className="font-bold text-lg text-gray-900">${(costData.cost_breakdown?.infrastructure || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Profit Estimate */}
          <div className={`rounded-xl p-6 ${costData.totals?.estimated_profit >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Estimated {costData.totals?.estimated_profit >= 0 ? 'Profit' : 'Loss'}</p>
                <p className={`text-3xl font-bold ${costData.totals?.estimated_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${Math.abs(costData.totals?.estimated_profit || 0).toLocaleString()}
                </p>
              </div>
              {costData.totals?.estimated_profit >= 0 ? (
                <CheckCircle className="h-12 w-12 text-green-500" />
              ) : (
                <AlertTriangle className="h-12 w-12 text-red-500" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transaction Ledger Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Transaction Ledger</h3>
            <span className="text-sm text-gray-500">Source of Truth</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      No transactions found. Transactions will appear here as users recharge Emeralds and send gifts.
                    </td>
                  </tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(tx.timestamp).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{tx.user_name || tx.user_id?.slice(0, 8)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">
                          {tx.transaction_type?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">{tx.token_type}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{tx.gross_amount}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-500">{tx.platform_fee}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-emerald-600">{tx.net_amount}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          tx.status === 'completed' ? 'bg-green-100 text-green-700' :
                          tx.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                          {tx.source}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Manager Tab */}
      {activeTab === 'ai-manager' && (
        <div className="space-y-6">
          {/* AI Disclaimer */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
            <Brain className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <p className="text-sm text-purple-800 font-medium">AI Manager - Advisory Only</p>
              <p className="text-sm text-purple-700">
                These insights are generated from aggregated, anonymized data. No PII is processed. 
                All suggestions require human approval before implementation. No automated actions are taken.
              </p>
            </div>
          </div>

          {/* Insights Grid */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              AI-Generated Insights
            </h3>
            
            {aiInsights.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center">
                <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No insights available. Generate test data to see AI recommendations.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {aiInsights.map((insight, index) => (
                  <InsightCard key={index} insight={insight} />
                ))}
              </div>
            )}
          </div>

          {/* AI Guardrails */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              AI Guardrails
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Advisory only - No automated execution</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">No automated pricing changes</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">No user-level targeting</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Human approval required for all changes</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialDashboardPage;
